// src/pages/companies/CompaniesTable.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Row, Col, CardHeader, CardBody, Spinner, Alert, Input, Button, Badge
} from "reactstrap";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { COMPANY_TYPE_LABEL_MAP, COMPANY_TYPE_BADGE_COLORS } from "../../constants/companyTypes";
import { API } from "../../api/urls";
import useAnimatedPlaceholder, { SEARCH_FIELDS } from "../../hooks/useAnimatedPlaceholder";
import { CompanyDeleteModal } from "../modals/company/CompanyDeleteModal";
import TruncatedText from "../../components/Common/TruncatedText"
import FeatherIcon from "feather-icons-react";
import { formatLicenseDate } from "../../utils/subscriptionDates";

// Ortak kart bileşeni (UsersTable ile aynı)
const TableCard = ({ className = "", children, ...rest }) => (
    <div
        className={`card rounded-4 border-0 shadow-lg ${className}`}
        style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
        {...rest}
    >
        {children}
    </div>
);

const NAVY = "#221C5D";
const ORANGE = "#E56A22";

const CompaniesTable = () => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");

    // Sıralama (lisans bitiş tarihi vb.) — null iken backend varsayılanı (createdAt desc)
    const [sortBy, setSortBy] = useState(null);
    const [sortDir, setSortDir] = useState("asc");

    const [editingCompany, setEditingCompany] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [detailCompany, setDetailCompany] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [deleteCompany, setDeleteCompany] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);

    const isFirstSearch = useRef(true);
    const searchRef = useRef(null);
    const animatedPlaceholder = useAnimatedPlaceholder(SEARCH_FIELDS.companies, { prefix: "Ara: " });
    const navigate = useNavigate();

    const styles = `
    .companies-modern .table thead tr { background-color: #f9f9f9; }
    .companies-modern .table th { border: 0; color: #6c757d; font-weight: 600; }
    .companies-modern .table td { vertical-align: middle; }
    .companies-modern .table-hover tbody tr:hover { background: #fcfcfd; }
    .companies-modern .search-input { width: 250px; }
    .companies-modern .header-actions { gap: .5rem; }
    .companies-modern .btn-rounded { border-radius: 999px; }
    .companies-modern .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .companies-modern .btn-navy:hover { filter: brightness(1.03); }
    .companies-modern .btn-orange { background: ${ORANGE}; border-color: ${ORANGE}; color: #fff; }
    .companies-modern .btn-orange:hover { filter: brightness(1.03); }
    .companies-modern .action-btn { border-radius: 999px; padding: .35rem .6rem; }
    .companies-modern .pill-input, .companies-modern .pill-select {
      border-radius: 999px !important; padding: .45rem .9rem;
    }
    /* Kurumsal pagination */
    .companies-modern .corporate-pagination .page-link { color: ${NAVY}; border-color: ${ORANGE}; }
    .companies-modern .corporate-pagination .page-item.active .page-link {
      background-color: ${NAVY}; border-color: ${NAVY}; color: #fff;
    }
    .companies-modern .corporate-pagination .page-link:hover { border-color: ${NAVY}; color: ${NAVY}; }
    .companies-modern .code-dark { color: #000 !important; }

    @media (max-width: 576px) {
      .companies-modern .search-input { width: 100%; }
      .companies-modern .table { font-size: 12px; }
      .companies-modern .table td, .companies-modern .table th { padding: .5rem; }
    }
    
    .companies-modern .page-size-wrap { display:flex; align-items:center; gap:.5rem; white-space:nowrap; }
    .companies-modern .page-size-select { min-width:70px; padding-right:1.75rem; }
  `;

    const companyTypeColor = (t) => COMPANY_TYPE_BADGE_COLORS[t] || "#B0B7C3";

    const CompanyTypeBadge = ({ type }) => (
        <span
            className="badge border-0"
            style={{
                borderRadius: 999,
                padding: ".35rem .5rem",
                backgroundColor: companyTypeColor(type),
                color: "#fff"
            }}
        >
    {getCompanyTypeInTurkish(type)}
  </span>
    );

    document.title = "Firmalar | Tahsilat360";

    const fetchCompanies = async (page = 0, size = 10, search = "", first = false, sBy = sortBy, sDir = sortDir) => {
        try {
            setError(null);
            if (first) setLoading(true); else setRefreshing(true);

            const params = { page, size };
            if (search) params.searchTerm = search;
            if (sBy) { params.sortBy = sBy; params.sortDir = sDir; }

            const { data } = await axios.get(API.admin_companies_list, {
                params,
                headers: {}
            });

            if (data.statusCode === 200) {
                const { content, totalElements, totalPages, page: cp, size: ps } = data.data;
                setCompanies(content);
                setTotalElements(totalElements);
                setTotalPages(totalPages);
                setCurrentPage(cp);
                setPageSize(ps);
            } else {
                setError("Firmalar yüklenemedi");
            }
        } catch (err) {
            if (err.response?.status === 401 || err.response?.status === 403) {
                navigate("/login");
            } else {
                setError(err.response?.data?.message || "Şirketler yüklenirken bir hata oluştu");
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchCompanies(0, pageSize, "", true);
    }, []);

    useEffect(() => {
        if (isFirstSearch.current) { isFirstSearch.current = false; return; }
        const t = setTimeout(() => fetchCompanies(0, pageSize, searchTerm), 500);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const handlePageChange = (p) => {
        if (p >= 0 && p < totalPages) {
            setCurrentPage(p);
            fetchCompanies(p, pageSize, searchTerm);
        }
    };
    const handlePageSizeChange = (sz) => {
        setPageSize(sz);
        setCurrentPage(0);
        fetchCompanies(0, sz, searchTerm);
    };

    // Kolon başlığına tıklayınca sırala (aynı kolon → yön değiştir)
    const handleSort = (field) => {
        const nextDir = sortBy === field ? (sortDir === "asc" ? "desc" : "asc") : "asc";
        setSortBy(field);
        setSortDir(nextDir);
        setCurrentPage(0);
        fetchCompanies(0, pageSize, searchTerm, false, field, nextDir);
    };

    const sortIcon = (field) =>
        sortBy === field
            ? (sortDir === "asc" ? "bx bx-sort-up" : "bx bx-sort-down")
            : "bx bx-sort-alt-2";

    const getPages = () => {
        const arr = [];
        const max = 5;
        let start = Math.max(0, currentPage - Math.floor(max / 2));
        let end = Math.min(totalPages - 1, start + max - 1);
        if (end - start + 1 < max) start = Math.max(0, end - max + 1);
        for (let i = start; i <= end; i++) arr.push(i);
        return arr;
    };

    const formatDate = (d) => formatLicenseDate(d);

    const handleDelete = async (companyId) => {
        try {
            await axios.delete(API.admin_company(companyId));
            fetchCompanies(currentPage, pageSize, searchTerm);
        } catch (err) {
            if (err.response?.status === 401 || err.response?.status === 403) {
                navigate("/login");
            } else {
                setError(err.response?.data?.message || "Şirket silinemedi");
            }
        } finally {
            setDeleteModalOpen(false);
            setDeleteCompany(null);
        }
    };

    const handleCreateSuccess = () => {
        setCurrentPage(0);
        fetchCompanies(0, pageSize, searchTerm);
        setCreateModalOpen(false);
    };

    const getCompanyTypeInTurkish = (companyType) =>
         COMPANY_TYPE_LABEL_MAP[companyType] || companyType;
    const getWorkingAreaInTurkish = (workingArea) => ({
        INFORMATION_TECHNOLOGY: "Bilişim ve Yazılım",
        MANUFACTURING: "Üretim ve İmalat",
        RETAIL: "Perakende Satış",
        HEALTHCARE: "Sağlık ve Tıp",
        FINANCE: "Finans ve Sigorta",
        EDUCATION: "Eğitim Hizmetleri",
        AUTOMOTIVE: "Otomotiv Sektörü",
        FOOD_AND_BEVERAGE: "Gıda ve İçecek",
        CONSTRUCTION: "İnşaat Sektörü",
        LOGISTICS: "Lojistik ve Nakliye",
        ENERGY: "Enerji Üretimi ve Dağıtımı",
        OTHER: "Diğer Sektörler",
    }[workingArea] || workingArea);

    return (
        <div className="page-content companies-modern">
            <style>{styles}</style>
            <div className="container-fluid">
                <Row>
                    <Col>
                        <TableCard>
                            <CardHeader className="bg-white border-0 rounded-top-4">
                                {/* DESKTOP header */}
                                <div className="d-none d-md-block">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <h5 className="mb-1 fw-bold text-dark">Firma Yönetimi</h5>
                                            <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                                                {totalElements > 0 ? `Toplam ${totalElements} firma bulundu` : "Firma bulunamadı"}
                                            </p>
                                        </div>
                                        <Button
                                            className="d-flex align-items-center gap-1 btn-rounded btn-navy"
                                            onClick={() => navigate("/companies/create")}
                                        >
                                            <i className="bx bx-plus" /> Yeni Firma
                                        </Button>
                                    </div>
                                    <div className="d-flex justify-content-end">
                                        <Input
                                            innerRef={searchRef}
                                            type="search"
                                            placeholder={animatedPlaceholder}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="search-input pill-input"
                                            inputMode="search"
                                            autoComplete="off"
                                            autoCorrect="off"
                                            spellCheck={false}
                                        />
                                    </div>
                                </div>

                                {/* MOBILE header */}
                                <div className="d-md-none">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h5 className="mb-0 fw-semibold">Firmalar</h5>
                                        <Button
                                            size="sm"
                                            className="d-flex align-items-center gap-1 btn-rounded btn-navy"
                                            onClick={() => navigate("/companies/create")}
                                        >
                                            <i className="bx bx-plus" /> Ekle
                                        </Button>
                                    </div>
                                    <p className="mb-2 text-muted" style={{ fontSize: ".9rem" }}>
                                        {totalElements > 0 ? `Toplam ${totalElements} firma` : "Firma bulunamadı"}
                                    </p>
                                    <Input
                                        innerRef={searchRef}
                                        type="search"
                                        placeholder={animatedPlaceholder}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pill-input"
                                        inputMode="search"
                                        autoComplete="off"
                                        autoCorrect="off"
                                        spellCheck={false}
                                    />
                                </div>
                            </CardHeader>

                            <CardBody className="pt-2">
                                {error && (
                                    <Alert color="danger" className="mb-3 rounded-4">
                                        <i className="bx bx-error-circle me-2" />
                                        {error}
                                        <button
                                            className="btn btn-sm btn-outline-danger ms-2 btn-rounded"
                                            onClick={() => fetchCompanies(currentPage, pageSize, searchTerm)}
                                        >
                                            Tekrar Dene
                                        </button>
                                    </Alert>
                                )}

                                {loading ? (
                                    <div className="text-center p-5">
                                        <Spinner size="lg" color="primary" />
                                        <h6 className="mt-3 text-muted">Firmalar yükleniyor...</h6>
                                    </div>
                                ) : (
                                    <>
                                        <div className={`d-flex justify-content-between align-items-center mb-2 ${refreshing ? 'opacity-50' : ''}`}>
                                            <div className="text-muted" style={{ fontSize: ".9rem" }}>
                                                {totalElements > 0 && (
                                                    <>Toplam <strong>{totalElements}</strong> kayıt • Sayfa <strong>{currentPage + 1}</strong> / {totalPages}</>
                                                )}
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <button
                                                    className="btn btn-light btn-sm btn-rounded"
                                                    onClick={() => fetchCompanies(currentPage, pageSize, searchTerm)}
                                                    title="Yenile"
                                                >
                                                    <i className={`bx bx-refresh ${refreshing ? 'bx-spin' : ''}`} />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="table-responsive rounded-4 border">
                                            <Table className="table table-hover mb-0">
                                                <Thead>
                                                    <Tr>
                                                        <Th>Firma Adı</Th>
                                                        <Th className="d-none d-md-table-cell">Alt Alan Adı</Th>
                                                        <Th className="d-none d-sm-table-cell">Firma Türü</Th>
                                                        <Th className="d-none d-xxl-table-cell">Firma Kodu</Th>
                                                        <Th className="d-none d-lg-table-cell">İletişim Kişisi</Th>
                                                        <Th className="d-none d-xl-table-cell">E-posta</Th>
                                                        <Th
                                                            className="d-none d-xl-table-cell"
                                                            style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                                                            onClick={() => handleSort("licenseStartDate")}
                                                            title="Lisans başlangıç tarihine göre sırala"
                                                        >
                                                            Lisans Başlangıç <i className={sortIcon("licenseStartDate")} />
                                                        </Th>
                                                        <Th
                                                            className="d-none d-xl-table-cell"
                                                            style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                                                            onClick={() => handleSort("licenseExpiryDate")}
                                                            title="Lisans bitiş tarihine göre sırala"
                                                        >
                                                            Lisans Bitiş <i className={sortIcon("licenseExpiryDate")} />
                                                        </Th>
                                                        <Th>Durum</Th>
                                                        <Th className="text-end">İşlemler</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {companies.length ? companies.map(c => (
                                                        <Tr key={c.id} className="border-bottom">
                                                            <Td>
                                                                <TruncatedText
                                                                    text={c.companyName}
                                                                    maxLength={20}
                                                                    className="fw-semibold text-dark"
                                                                />
                                                            </Td>
                                                            <Td className="d-none d-md-table-cell">{c.subdomain || "-"}</Td>
                                                            <Td className="d-none d-sm-table-cell"><CompanyTypeBadge type={c.companyType} /></Td>
                                                            <Td className="d-none d-xxl-table-cell"><code className="code-dark">{c.companyCode || "-"}</code></Td>
                                                            <Td className="d-none d-md-table-cell">
                                                                <TruncatedText text={c.contactFullName} maxLength={22} />
                                                            </Td>
                                                            <Td className="d-none d-xl-table-cell">
                                                                <TruncatedText text={c.contactEmail || "-"} maxLength={22} />
                                                            </Td>
                                                            <Td className="d-none d-xl-table-cell">{formatDate(c.licenseStartDate)}</Td>
                                                            <Td className="d-none d-xl-table-cell">{formatDate(c.licenseExpiryDate)}</Td>
                                                            <Td>
                                <span
                                    className="badge border-0"
                                    style={{ borderRadius: 999, backgroundColor: c.active ? "#71C30B" : "#E07A7A", color: "#fff" }}
                                >
                                  {c.active ? "Aktif" : "Pasif"}
                                </span>
                                                            </Td>
                                                            <Td className="text-end">
                                                                <div className="d-flex gap-1 justify-content-end flex-wrap">
                                                                    <button
                                                                        className="btn btn-sm action-btn btn-navy"
                                                                        title="Düzenle"
                                                                        onClick={() => navigate(`/companies/${c.id}/edit`)}
                                                                    >
                                                                        <i className="bx bx-edit" />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm action-btn btn-orange"
                                                                        title="Detay"
                                                                        onClick={() => navigate(`/companies/${c.id}`)}
                                                                    >
                                                                        <i className="bx bx-show" />
                                                                    </button>
                                                                    {/* Kullanıcı listesi companyId parametresiyle süzülür. */}
                                                                    <button
                                                                        className="btn btn-sm action-btn"
                                                                        style={{ background: "#221C5D", borderColor: "#221C5D", color: "#fff" }}
                                                                        title="Bu firmanın kullanıcılarını gör"
                                                                        onClick={() => navigate(`/users?companyId=${c.id}`)}
                                                                    >
                                                                        <FeatherIcon icon="users" size={14} style={{ verticalAlign: "middle" }} />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm action-btn"
                                                                        style={{ background: "#dc3545", borderColor: "#dc3545", color: "#fff" }}
                                                                        title="Sil"
                                                                        onClick={() => { setDeleteCompany(c); setDeleteModalOpen(true); }}
                                                                    >
                                                                        <i className="bx bx-trash" />
                                                                    </button>
                                                                </div>
                                                            </Td>
                                                        </Tr>
                                                    )) : (
                                                        <Tr>
                                                            <Td colSpan="12" className="text-center py-4">
                                                                <div className="text-muted">
                                                                    <i className="bx bx-building-house display-4 d-block mb-2" />
                                                                    Firma bulunamadı
                                                                    {searchTerm && (
                                                                        <div className="mt-2">
                                                                            <small>Arama teriminizi değiştirmeyi deneyin</small>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Td>
                                                        </Tr>
                                                    )}
                                                </Tbody>
                                            </Table>
                                        </div>

                                        {/* Pagination (UsersTable ile aynı görünüm) */}
                                        {totalElements > 0 && (
                                            <div className="d-flex justify-content-between align-items-center mt-3">
                                                <div className="text-muted" style={{ fontSize: ".9rem" }}>
                                                    {totalElements} kayıttan {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalElements)} arası gösteriliyor
                                                </div>

                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="d-flex align-items-center gap-2 page-size-wrap">
                                                        <span className="text-muted" style={{ fontSize: ".9rem" }}>Sayfa başına</span>
                                                        <select
                                                            className="form-select form-select-sm rounded-pill page-size-select"
                                                            value={pageSize}
                                                            onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                                                        >
                                                            {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                                                        </select>
                                                    </div>

                                                    <nav>
                                                        <ul className="pagination pagination-sm mb-0 corporate-pagination">
                                                            <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                                    disabled={currentPage === 0}
                                                                >
                                                                    <i className="bx bx-chevron-left" />
                                                                </button>
                                                            </li>
                                                            {getPages().map(p => (
                                                                <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                                                                    <button className="page-link" onClick={() => handlePageChange(p)}>
                                                                        {p + 1}
                                                                    </button>
                                                                </li>
                                                            ))}
                                                            <li className={`page-item ${currentPage >= totalPages - 1 ? "disabled" : ""}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                                    disabled={currentPage >= totalPages - 1}
                                                                >
                                                                    <i className="bx bx-chevron-right" />
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </nav>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardBody>

                            <CompanyDeleteModal
                                isOpen={deleteModalOpen}
                                toggle={() => setDeleteModalOpen(!deleteModalOpen)}
                                company={deleteCompany}
                                onConfirm={handleDelete}
                            />
                        </TableCard>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default CompaniesTable;